import type { Locale, Permission, SessionUser } from '@hm/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, use, useCallback } from 'react'
import { api } from './api'

const SESSION_KEY = ['session'] as const

export function useSessionQuery() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: () => api.get<{ user: SessionUser | null }>('/api/auth/session'),
    // The session is the app's root dependency; refetching it on every window
    // focus would make the whole tree flicker for no benefit.
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useBootstrapStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => api.get<{ needed: boolean }>('/api/bootstrap'),
    enabled,
    retry: false,
  })
}

export const SessionContext = createContext<SessionUser | null>(null)

export function useSession(): SessionUser {
  const user = use(SessionContext)
  if (!user) throw new Error('useSession used outside an authenticated route')
  return user
}

/** Permission check for hiding UI. The server enforces the same rule regardless. */
export function useCan(): (permission: Permission) => boolean {
  const user = useSession()
  return (permission) => user.permissions.includes(permission)
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      /**
       * Order matters, and `clear()` on its own is not enough.
       *
       * `clear()` empties the cache without telling mounted observers, so the app
       * stayed fully rendered after signing out while every poll behind it turned
       * into a 401 — the previous user's screen left on a shared counter.
       *
       * Writing the session explicitly is what unmounts the authenticated tree;
       * dropping everything else afterwards is what stops the next person to sign
       * in on this tab seeing a flash of the last one's data.
       */
      queryClient.setQueryData(SESSION_KEY, { user: null })
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_KEY[0] })
    },
  })
}

/**
 * The preferences that live on the account rather than on the device.
 *
 * They go through one call because they are one endpoint and one row; the language
 * and walkthrough halves are fire-and-forget on purpose — the screen has already
 * changed by the time this runs, and a failed write means the next device inherits
 * the old answer, not that this one is wrong.
 */
function usePreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: { advancedMode?: boolean; locale?: Locale; toursSeen?: string[] }) =>
      api.post('/api/auth/preferences', patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
  })
}

export function useAdvancedMode() {
  const user = useSession()
  const mutation = usePreferences()
  return {
    enabled: user.advancedMode,
    toggle: () => mutation.mutate({ advancedMode: !user.advancedMode }),
  }
}

/**
 * Remember the language on the account too, so it follows the person to a new
 * device. Stable across renders because the effect that calls it lists it as a
 * dependency, and a fresh closure each render would make that effect loop.
 */
export function useRememberLocale(): (locale: Locale) => void {
  const mutation = usePreferences()
  const { mutate } = mutation
  return useCallback((locale: Locale) => mutate({ locale }), [mutate])
}

/**
 * Which walkthroughs this person has been through, sent whole rather than as an
 * append — which is also what makes "start the guide over" the same call with an
 * empty list. Two devices mid-tour could lose one tick between them; the cost of
 * that is a screen somebody has already read, and the alternative is a
 * merge protocol for a checklist.
 */
export function useRememberTours(): (toursSeen: string[]) => void {
  const { mutate } = usePreferences()
  return useCallback((toursSeen: string[]) => mutate({ toursSeen }), [mutate])
}

export { SESSION_KEY }
